import React, { useState, useRef, useEffect } from 'react';
import { Box, Chip, CircularProgress, Divider, IconButton, Paper, Portal, Typography } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import Draggable from 'react-draggable';

// The floating, non-modal window the AI guidance features render their results
// into. Extracted from NoteFormv2 when forms needed the same treatment.
//
// It owns the chrome that is identical everywhere -- the draggable frame, the
// remembered position, the score chip, the loading and error states, and the
// tips list -- and nothing about what is being evaluated. Callers pass their own
// sections as children, because a case note's Service Strategies and a form's
// field-level findings have nothing in common below the tips.
//
// Non-modal on purpose: the note or form stays fully editable while it is open,
// which is the point of showing guidance at all.

// Above MUI's modal layer (1300) so the window floats over the note dialogs.
const GUIDANCE_WINDOW_ZINDEX = 1400;
// Height reserved for the fixed "Tours" launcher pinned to the bottom-left of every page.
const TOURS_LAUNCHER_CLEARANCE = 88;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const scoreColor = (score) => (score >= 80 ? '#4caf50' : score >= 50 ? '#ff9800' : '#f44336');

/**
 * @param {boolean}  open
 * @param {boolean}  loading
 * @param {string}   error         message to show instead of results
 * @param {number}   score         0-100; the header chip is hidden when null
 * @param {string}   title         header text
 * @param {string}   loadingText
 * @param {string}   storageKey    localStorage key for the remembered position.
 *   Give each surface its own so dragging one does not move the other.
 * @param {string[]} tips
 * @param {boolean}  hasResult     gates the body; lets a caller show results with no tips
 * @param {Function} onClose
 * @param {Node}     children      surface-specific sections, rendered under the tips
 */
const GuidanceWindow = ({
    open,
    loading,
    error,
    score,
    title = 'AI Guidance',
    loadingText = 'Evaluating...',
    storageKey = 'guidanceWindowPosition',
    tips = [],
    hasResult = false,
    onClose,
    children,
}) => {
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const windowRef = useRef(null);

    // A position saved on a bigger screen can land off-screen here, so pull the
    // window back into view whenever it opens or the viewport changes.
    const keepOnScreen = (pos) => {
        const el = windowRef.current;
        if (!el) return pos;
        const rect = el.getBoundingClientRect();
        const maxTop = Math.max(0, window.innerHeight - TOURS_LAUNCHER_CLEARANCE - rect.height);
        return {
            x: pos.x + (clamp(rect.left, 0, Math.max(0, window.innerWidth - rect.width)) - rect.left),
            y: pos.y + (clamp(rect.top, 0, maxTop) - rect.top),
        };
    };

    useEffect(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) setPosition(JSON.parse(saved));
        } catch (err) {
            console.log(err);
        }
    }, [storageKey]);

    useEffect(() => {
        if (!open) return;
        const settle = () => setPosition((pos) => {
            const next = keepOnScreen(pos);
            return next.x === pos.x && next.y === pos.y ? pos : next;
        });
        settle();
        window.addEventListener('resize', settle);
        return () => window.removeEventListener('resize', settle);
    }, [open]);

    if (!open) return null;

    return (
        <Portal>
            <Draggable
                handle=".guidance-drag-handle"
                cancel=".guidance-no-drag"
                nodeRef={windowRef}
                position={position}
                onStop={(_e, data) => {
                    const next = keepOnScreen({ x: data.x, y: data.y });
                    setPosition(next);
                    try {
                        localStorage.setItem(storageKey, JSON.stringify(next));
                    } catch (err) {
                        console.log(err);
                    }
                }}
            >
                <Paper
                    ref={windowRef}
                    elevation={8}
                    sx={{
                        position: 'fixed',
                        left: 24,
                        bottom: `${TOURS_LAUNCHER_CLEARANCE}px`,
                        zIndex: GUIDANCE_WINDOW_ZINDEX,
                        width: 380,
                        maxWidth: 'calc(100vw - 48px)',
                        maxHeight: '70vh',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid #e0e7ef',
                    }}
                >
                    <Box
                        className="guidance-drag-handle"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            backgroundColor: '#eef2ff',
                            borderBottom: '1px solid #e0e7ef',
                            cursor: 'move',
                            userSelect: 'none',
                        }}
                    >
                        <DragIndicatorIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                        <AutoFixHighIcon sx={{ fontSize: 20, color: '#5c6bc0' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', flex: 1 }}>
                            {title}
                        </Typography>
                        {!loading && !error && hasResult && score !== null && score !== undefined && (
                            <Chip
                                label={`${score}%`}
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    backgroundColor: scoreColor(score),
                                    color: '#fff',
                                    height: 22,
                                    fontSize: '0.75rem',
                                }}
                            />
                        )}
                        <IconButton
                            className="guidance-no-drag"
                            size="small"
                            aria-label="close guidance"
                            onClick={onClose}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 1.5, overflowY: 'auto' }}>
                        {loading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 2 }}>
                                <CircularProgress size={20} sx={{ color: '#5c6bc0' }} />
                                <Typography variant="body2" sx={{ color: '#546e7a', fontWeight: 500 }}>
                                    {loadingText}
                                </Typography>
                            </Box>
                        )}

                        {!loading && error && (
                            <Typography variant="body2" color="error">{error}</Typography>
                        )}

                        {!loading && !error && hasResult && (
                            <>
                                {tips.map((tip, index) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                        <TipsAndUpdatesIcon sx={{ fontSize: 18, color: '#f59e0b', mt: 0.2 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', lineHeight: 1.45 }}>
                                            {tip}
                                        </Typography>
                                    </Box>
                                ))}

                                {tips.length > 0 && children ? <Divider sx={{ my: 1.5 }} /> : null}

                                {children}
                            </>
                        )}
                    </Box>
                </Paper>
            </Draggable>
        </Portal>
    );
};

export default GuidanceWindow;
